/**
 * GRC catalog connector manifests.
 *
 * Registers every GRC ingest path as a {@link ConnectorManifest}. The
 * picker UI emerges from filtering — adding a new connector is a
 * one-file change (register here + adapter module). Adapter modules are
 * lazy-imported so they don't bloat the initial bundle.
 *
 * v1: OSCAL importer (NIST's open compliance interchange format).
 * v2+: Vanta read-sync, Drata read-sync, custom-framework CSV.
 */
import { registerConnector } from '../../../core/data-profiles/connectorRegistry';

export function registerGrcCatalogConnectors(): void {
  safeRegister({
    id: 'oscal-catalog',
    label: 'OSCAL Catalog',
    description: 'Import a SOC 2 / ISO 27001 / NIST 800-53 / HIPAA OSCAL JSON catalog.',
    iconRef: 'assets/connectors/oscal.svg',
    format: 'file',
    inputHint: 'OSCAL JSON (NIST open compliance format)',
    folderKinds: ['grc'],
    targets: ['folder'],
    compatibleProfileIds: ['grc-control'],
    source: {
      system: 'oscal',
      defaultIntent: 'control_risk_register',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    status: 'stable',
    adapter: () =>
      import('./oscal/OscalImporter').then((m) => new m.OscalImporter() as never),
  });

  safeRegister({
    id: 'vanta-evidence',
    label: 'Vanta evidence',
    description: 'Sync Vanta evidence as GRC evidence records with framework references.',
    iconRef: 'assets/connectors/vanta.svg',
    format: 'oauth',
    inputHint: 'Vanta OAuth connection',
    folderKinds: ['grc'],
    targets: ['folder'],
    compatibleProfileIds: ['grc-control'],
    source: {
      system: 'vanta',
      defaultIntent: 'control_risk_register',
      emitsDeltas: true,
      retentionClass: 'audit_retained',
      volume: 'high',
    },
    status: 'beta',
    adapter: () =>
      import('./vendors/VendorEvidenceAdapter').then(
        (m) => new m.VendorEvidenceAdapter('vanta') as never,
      ),
  });

  safeRegister({
    id: 'drata-evidence',
    label: 'Drata evidence',
    description: 'Sync Drata evidence as GRC evidence records with framework references.',
    iconRef: 'assets/connectors/drata.svg',
    format: 'oauth',
    inputHint: 'Drata OAuth connection',
    folderKinds: ['grc'],
    targets: ['folder'],
    compatibleProfileIds: ['grc-control'],
    source: {
      system: 'drata',
      defaultIntent: 'control_risk_register',
      emitsDeltas: true,
      retentionClass: 'audit_retained',
      volume: 'high',
    },
    status: 'beta',
    adapter: () =>
      import('./vendors/VendorEvidenceAdapter').then(
        (m) => new m.VendorEvidenceAdapter('drata') as never,
      ),
  });
}

type RegisterArgs = Parameters<typeof registerConnector>[0];

function safeRegister(manifest: RegisterArgs): void {
  try {
    registerConnector(manifest);
  } catch (err) {
    if (err instanceof Error && err.message.includes('duplicate connector id')) return;
    throw err;
  }
}
